import type { IAhuBoardSection, IAhuComponentRow, ICmsAhuDoc } from 'models';
import {
  COMPONENT_FORM_CONFIGS,
  resolveComponentType,
} from '../componentFormConfigs';
import type { ComponentTypeName } from '../componentFormTypes';
import FanForm from './FanForm';
import CoilForm from './CoilForm';
import FilterForm from './FilterForm';
import EliminatorForm from './EliminatorForm';
import EvapPackForm from './EvapPackForm';
import GasHeaterForm from './GasHeaterForm';
import SilencerForm from './SilencerForm';
import EnergyWheelForm from './EnergyWheelForm';
import PlateHeatExchangerForm from './PlateHeatExchangerForm';
import UdcForm from './UdcForm';

/**
 * Phase 6e2a — the §50.0 form factory (tab.components.js:160–242) as the
 * mount point inside 6e1's TabComponents row shells: resolves the type
 * (uds→user_defined_component, plate→plate_heat_exchanger,
 * rotary→energy_wheel; heat/heatex/wheel phantoms filtered upstream),
 * resolves the dual-tunnel partner via the bound-section rule
 * (other.sitting_on_id === section.id — primary hosts only, §7.5), and
 * mounts the per-type form. The dotted preloader stays until the form's
 * first paint (§50.0 placeholder contract — parity 307).
 *
 * [NEG]#111 honored: plenum/split forms are DEAD (never created by §20 #46)
 * — no rows reach here for them (factory returns the preloader for unknown
 * types, same as the legacy unresolved-require path).
 */

export interface ComponentFormProps {
  row: IAhuComponentRow;
  section: IAhuBoardSection;
  section2: IAhuBoardSection | null;
  cms: ICmsAhuDoc | undefined;
}

const FORMS: Record<ComponentTypeName, (props: ComponentFormProps) => JSX.Element> = {
  fan: FanForm,
  coil: CoilForm,
  filter: FilterForm,
  eliminator: EliminatorForm,
  evap_pack: EvapPackForm,
  gas_heater: GasHeaterForm,
  silencer: SilencerForm,
  energy_wheel: EnergyWheelForm,
  plate_heat_exchanger: PlateHeatExchangerForm,
  user_defined_component: UdcForm,
};

const DUAL_TYPES: ReadonlySet<ComponentTypeName> = new Set([
  'energy_wheel',
  'plate_heat_exchanger',
]);

const ComponentFormHost = ({
  row,
  section,
  sections,
  cms,
}: {
  row: IAhuComponentRow;
  section: IAhuBoardSection;
  sections: IAhuBoardSection[];
  cms: ICmsAhuDoc | undefined;
}) => {
  const type = resolveComponentType(
    String(row.type ?? section.name_on_canvas ?? '')
  );
  if (!type || !COMPONENT_FORM_CONFIGS[type]) {
    return <div className="preloader dotted-preloader active" />;
  }
  const section2 = DUAL_TYPES.has(type)
    ? sections.find((s) => s.sitting_on_id === section.id) ?? null
    : null;
  const Form = FORMS[type];
  return <Form row={row} section={section} section2={section2} cms={cms} />;
};

export default ComponentFormHost;
